<script setup lang="ts">
/**
 * UI-kit component
 * Category: Headers
 * Name: HeaderBanner
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=4194-16216
 *
 * The promo card on the right of the DESKTOP dropdown menu — fixed 332x370
 * bounds a campaign visual is designed to (oversize artwork is cropped, the
 * frame never resizes). The WHOLE card is one link; the Button inside is purely
 * visual (tabindex -1) — two nested targets are impossible, so block hover
 * drives the button's hover look. Default content is the "First Time Here?"
 * card on sunshine/200; a custom visual (image) replaces it in the same
 * bounds. Decorative artwork uses raw image fills — exempt from token audit.
 * Local implementation of the Figma DS component missing from @brighterly/lib-lumee-ui.
 */
// types
import type { HeaderBannerProps } from '../types/interfaces'
// assets
import defaultArt from '../images/banner-default-art.png'
import defaultArtWebp from '../images/banner-default-art.webp'
// components
import { Button } from '@brighterly/lib-lumee-ui'
import { AppImage } from '@UI/Images/AppImage'

defineOptions({
  name: 'HeaderBanner',
})

withDefaults(defineProps<HeaderBannerProps>(), {
  description: '',
  image: null,
})
</script>

<template>
  <a
    class="header-banner"
    :href="href"
  >
    <AppImage
      v-if="image"
      class="header-banner__image"
      :media="image"
      sizes="332px"
      alt=""
    />
    <span
      v-else
      class="header-banner__card"
    >
      <picture>
        <source
          type="image/webp"
          :srcset="defaultArtWebp"
        >
        <img
          class="header-banner__art"
          :src="defaultArt"
          alt=""
          width="396"
          height="402"
          loading="lazy"
          decoding="async"
        >
      </picture>
      <span class="header-banner__text">
        <span class="header-banner__title btly-text-heading-m">{{ title }}</span>
        <span
          v-if="description"
          class="header-banner__description btly-text-body-m"
        >{{ description }}</span>
      </span>
      <Button
        class="header-banner__cta"
        view="tertiary-accent"
        size="lg"
        trailing-icon="arrow-right"
        :text="buttonText"
        tabindex="-1"
      />
    </span>
  </a>
</template>

<style lang="scss" scoped src="../styles/_header-banner.scss" />
